/**
 * [TD-47.2a + TD-47.5] UnifiedDraftShell - shared shell for both auction +
 * snake.
 *
 * v1.2-TD-47.5 Layout Rebalance: replaces the single-slot `renderCenterPane`
 * + `renderRightPane` model with a 5-named-sub-slot model that owns split-
 * ratio layout centrally:
 *
 *   • Center pane: top action surface (CenterTop) + middle Canvas slot
 *     (CenterMiddle, optional but always present in v1.3). Bottom slot
 *     was retired in v1.3 - chat now mounts at right-bottom for both formats.
 *   • Right pane: top tabbed "your data" panel + optional middle section
 *     (auction-only AI/Curly, shrink-0 RIGHT_AI_HEIGHT_PX) + bottom
 *     ChatPanel + Adam mount (50% flex in three-zone mode; legacy
 *     RIGHT_ADAM_HEIGHT_PX retained for any future two-zone consumers).
 *
 * Slot constants are tunable in one place (founder smoke can adjust without
 * touching consumer files).
 *
 * Slots (all optional but typically passed):
 *   renderTopBarCenter()    → ReactNode for the center of the top bar
 *   renderLeftPane()        → left pane content (col-span-3)
 *   renderCenterTop()       → center pane top section - the action surface
 *                              (flex 0 0 22% when CenterMiddle present;
 *                              flex-1 + vertical centering when absent)
 *   renderCenterMiddle()    → center pane middle section - Player Analysis
 *                              Canvas; claims flex 1 1 auto
 *   renderRightTop()        → right pane top section - the tabbed panel;
 *                              flex 1 1 50% when CenterMiddle present;
 *                              flex-1 otherwise
 *   renderRightMiddle()     → right pane middle section - auction-only
 *                              AI/Curly Panel; shrink-0 RIGHT_AI_HEIGHT_PX.
 *                              Snake passes null/undefined → div is omitted.
 *   renderRightBottom()     → right pane bottom section - ADAM·LIVE feed;
 *                              shrink-0 RIGHT_ADAM_HEIGHT_PX. Permanent, no
 *                              tab affordance (Gap 10 verdict A).
 *   renderCommissionerControls() → optional inline strip below top bar
 *   renderMobile()          → mobile fallback (lg:hidden); per spec §7
 *
 * Props:
 *   format               - "auction" | "snake" (test-id prefix + audit)
 *   leagueId             - required for modal mounts + bridge nav
 *   league               - full league row {name, settings, commissioner_id, ...}
 *   isConnected          - WS connection state
 *   draftIsComplete      - boolean derived at call site
 *   bridgeRecapUrl       - optional; if set + recapDismissed, auto-nav after 5s
 *   recapDismissed       - boolean from format context
 *   dismissRecap         - fn from format context
 *   mockDraftCompleteEvent + setMockDraftCompleteEvent - from format context
 *   isCommissioner       - boolean
 *   boardUrl             - string; orange BOARD ↗ button target
 *   formatBadgeText      - optional small uppercase chip rendered next to logo
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap, Wifi, WifiOff, ArrowLeft, Sun, Moon, Maximize2, Minimize2,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import ProfileButton from "../ProfileButton";
import AdminButton from "../AdminButton";
import LegalDisclaimer from "../LegalDisclaimer";
import MockDraftCompleteModal from "./MockDraftCompleteModal";
import DraftEndRecapModal from "./DraftEndRecapModal";

// [TD-47.5 Gap 1.D + Gap 2.C] Layout constants - tunable in one place.
// Founder smoke can adjust these without touching consumer files.
// [TD-47.6a-ui v1.3 Redline 3.d] CENTER_CHAT_HEIGHT_PX deleted. Both
// formats now mount chat at renderRightBottom under the v1.2 unified
// layout (percent-based 22/58 center + 50/50 right pane).
const CENTER_ACTION_HEIGHT_PX = 130;
const RIGHT_AI_HEIGHT_PX = 200;
const RIGHT_ADAM_HEIGHT_PX = 260;
// [TD-47.6a-ui Gap A.2] Three-zone center pane: when `renderCenterMiddle` is
// passed, the action surface (CenterTop) is fixed-height and the middle slot
// (e.g., PlayerAnalysisCanvas) gets flex-1.

export default function UnifiedDraftShell({
  format = "auction",
  leagueId,
  league,
  isConnected,
  draftIsComplete = false,
  bridgeRecapUrl = null,
  recapDismissed = false,
  dismissRecap,
  mockDraftCompleteEvent = null,
  setMockDraftCompleteEvent,
  isCommissioner = false,
  boardUrl,
  formatBadgeText = null,
  renderTopBarCenter,
  renderLeftPane,
  renderCenterTop,
  renderCenterMiddle,
  renderCenterBottom,
  renderRightTop,
  renderRightMiddle,
  renderRightBottom,
  renderCommissionerControls,
  renderMobile,
}) {
  const nav = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  // Auto-route to draft recap 5s after the bridge fires (gated on recapDismissed
  // - DraftEndRecapModal must complete first per RL-20 / HF3 P0-2).
  useEffect(() => {
    if (!bridgeRecapUrl || !recapDismissed) return;
    const t = setTimeout(() => { nav(bridgeRecapUrl); }, 5000);
    return () => clearTimeout(t);
  }, [bridgeRecapUrl, recapDismissed, nav]);

  // Fullscreen toggle (carried from DraftRoom Phase 2a.2-B §4.6).
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fsSupported =
    typeof document !== "undefined" && !!document.documentElement?.requestFullscreen;
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    if (!fsSupported) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };

  const leagueType = league?.settings?.league_type || "";
  const isMockLeague = leagueType.startsWith("MOCK_");

  // Mock-complete modal - defense-in-depth driven from state in addition to
  // the WS event, in case a reconnect/HMR loses the event.
  const shouldSurfaceMockModal =
    (draftIsComplete && isMockLeague && !bridgeRecapUrl) || !!mockDraftCompleteEvent;
  const isMockComplete = shouldSurfaceMockModal && recapDismissed;

  return (
    <div
      className="flex flex-col h-screen overflow-hidden"
      style={{ background: isDark ? "#111111" : "#F0F0F0" }}
      data-theme={isDark ? "dark" : "light"}
      data-testid={`${format}-draft-shell`}
    >
      {/* Top bar */}
      <div
        className="border-b h-12 flex items-center px-3 shrink-0 gap-3 z-30"
        style={{
          background: isDark ? "#1C1C1C" : "#FFFFFF",
          borderColor: isDark ? "#252525" : "#D8D8D8",
        }}
      >
        <button
          onClick={() => nav("/dashboard")}
          className="text-[#A0A0A0] hover:text-[#FF6B00] transition-colors"
          data-testid={`${format}-back-btn`}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 bg-[#FF6B00] rounded flex items-center justify-center">
            <Zap size={12} className="text-white" />
          </div>
          <span
            className="font-display font-black uppercase text-sm hidden sm:block"
            style={{ color: isDark ? "#F5F5F5" : "#0A0A0A" }}
          >
            {league?.name}
          </span>
        </div>
        {formatBadgeText && (
          <span
            className="font-display font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-[#FF6B00]/15 text-[#FF6B00]"
            data-testid={`${format}-format-badge`}
          >
            {formatBadgeText}
          </span>
        )}
        <div className="flex-1 hidden md:block">
          {renderTopBarCenter?.()}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid={`${format}-theme-toggle`}
            className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] transition-all"
          >
            {isDark
              ? <Sun size={13} className="text-[#F5F5F5]" />
              : <Moon size={13} className="text-[#0A0A0A]" />}
          </button>
          {fsSupported && (
            <button
              onClick={toggleFullscreen}
              data-testid={`${format}-fullscreen-btn`}
              className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/[0.06] hover:bg-white/[0.12] transition-all"
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen
                ? <Minimize2 size={13} className="text-[#F5F5F5]" />
                : <Maximize2 size={13} className="text-[#F5F5F5]" />}
            </button>
          )}
          <ProfileButton variant={isDark ? "dark" : "light"} />
          <AdminButton variant={isDark ? "dark" : "light"} />
          {isConnected
            ? <Wifi size={14} className="text-[#00C853]" />
            : <WifiOff size={14} className="text-[#FF4444] animate-pulse" />}
          <span
            className="font-body text-xs hidden sm:block"
            style={{ color: isDark ? "#A0A0A0" : "#404040" }}
          >
            {isConnected ? "Live" : "Reconnecting..."}
          </span>
        </div>
      </div>

      {/* Optional inline commissioner controls strip */}
      {renderCommissionerControls?.()}

      {/* Mock-complete modal */}
      {shouldSurfaceMockModal && recapDismissed && (
        <MockDraftCompleteModal
          leagueId={leagueId}
          leagueType={mockDraftCompleteEvent?.league_type || leagueType}
          isCommissioner={isCommissioner}
          onClose={() => setMockDraftCompleteEvent?.(null)}
        />
      )}

      {/* Draft-end recap modal */}
      {draftIsComplete && !recapDismissed && (
        <DraftEndRecapModal
          leagueId={leagueId}
          onDismiss={dismissRecap}
          surface="draft-room"
        />
      )}

      {/* Mobile fallback (per spec §7: format-specific until TD-10) */}
      <div className="lg:hidden flex-1 overflow-hidden">
        {renderMobile?.()}
      </div>

      {/* Desktop 3-pane */}
      <div
        className="hidden lg:grid grid-cols-12 gap-3 flex-1 p-3 overflow-hidden"
        data-testid={`${format}-shell-grid`}
      >
        <div
          className="col-span-3 lg:col-span-3 xl:col-span-2 2xl:col-span-2 flex flex-col gap-3 overflow-hidden min-h-0"
          data-testid={`${format}-left-pane`}
        >
          {renderLeftPane?.()}
        </div>

        <div
          className="col-span-6 lg:col-span-6 xl:col-span-7 2xl:col-span-8 flex flex-col gap-2 overflow-hidden min-h-0"
          data-testid={`${format}-center-pane`}
        >
          {/* Orange BOARD ↗ button - parity preserved across formats */}
          {boardUrl && (
            <button
              onClick={() => window.open(boardUrl, "_blank")}
              data-testid={`${format}-open-draft-board`}
              className="w-full py-1.5 bg-[#FF6B00]/10 hover:bg-[#FF6B00]/20 border border-[#FF6B00]/30 rounded-xl transition-all shrink-0 flex items-center justify-center gap-2"
            >
              <span className="font-display font-black text-sm uppercase tracking-[0.2em] text-[#FF6B00]">
                DRAFT BOARD
              </span>
              <span className="font-display font-bold text-sm text-[#FF6B00]/70">↗</span>
            </button>
          )}
          {/* [TD-47.5 Gap 1.D + TD-47.6a-ui v1.1 Redline 1] Center top - action
              surface. Two-zone mode (no renderCenterMiddle) keeps the flex-1 +
              vertically centered behavior. Three-zone mode (renderCenterMiddle
              present) switches to percent-based flex per v1.1: top ≈22%,
              middle takes remainder ≈58%, bottom ≈20%. */}
          <div
            className={
              renderCenterMiddle
                ? "shrink-0 min-h-0 overflow-hidden"
                : "flex-1 min-h-0 overflow-hidden flex flex-col justify-center"
            }
            style={renderCenterMiddle ? { flex: "0 0 22%" } : undefined}
            data-testid={`${format}-center-top`}
          >
            {renderCenterTop?.()}
          </div>
          {/* [TD-47.6a-ui Gap A.2 + v1.1 Redline 1] Center middle - additive
              Player Analysis Canvas slot. When present, claims the remaining
              ≈58% via flex: 1 1 auto. */}
          {renderCenterMiddle && (
            <div
              className="min-h-0 overflow-hidden"
              style={{ flex: "1 1 auto" }}
              data-testid={`${format}-center-middle`}
            >
              {renderCenterMiddle()}
            </div>
          )}
          {/* [TD-47.6a-ui v1.3 Redline 3.d] CenterBottom slot DELETED entirely.
              Both auction and snake now mount chat at renderRightBottom under
              the unified v1.2 layout. The previous `!renderCenterMiddle` snake
              guard + CENTER_CHAT_HEIGHT_PX constant are gone. */}
        </div>

        <div
          className="col-span-3 lg:col-span-3 xl:col-span-3 2xl:col-span-2 flex flex-col gap-3 overflow-hidden min-h-0"
          data-testid={`${format}-right-pane`}
        >
          {/* [TD-47.5 Gap 2.C + Gap 9.A + v1.2 Redline 1] Right top - tabbed
              "your data" panel. In three-zone center mode (auction v1.2)
              this takes 50% of the right pane vertical; the bottom 50% goes
              to ChatPanelWithFilter. In two-zone center mode (snake), this
              keeps flex-1 (full vertical minus the snake right-middle slot
              + right-bottom Adam feed). */}
          <div
            className={renderCenterMiddle ? "min-h-0 overflow-hidden" : "flex-1 min-h-0 overflow-hidden"}
            style={renderCenterMiddle ? { flex: "1 1 50%" } : undefined}
            data-testid={`${format}-right-top`}
          >
            {renderRightTop?.()}
          </div>
          {/* [TD-47.5 Gap 12.A] Middle slot - auction-only AI/Curly. Snake
              passes null → div omitted entirely (no wasted gap). */}
          {renderRightMiddle && (
            <div
              className="shrink-0 min-h-0 overflow-hidden"
              style={{ height: `${RIGHT_AI_HEIGHT_PX}px` }}
              data-testid={`${format}-right-middle`}
            >
              {renderRightMiddle()}
            </div>
          )}
          {/* [TD-47.5 Gap 11 + v1.2 Redline 1] Right bottom - permanent feed.
              v1.2 three-zone (auction): ChatPanelWithFilter at flex 1 1 50%.
              Two-zone (snake): legacy LiveActivityFeed at fixed
              RIGHT_ADAM_HEIGHT_PX. */}
          {renderRightBottom && (
            <div
              className="min-h-0 overflow-hidden"
              style={renderCenterMiddle ? { flex: "1 1 50%" } : { height: `${RIGHT_ADAM_HEIGHT_PX}px` }}
              data-testid={`${format}-right-bottom`}
            >
              {renderRightBottom()}
            </div>
          )}
        </div>
      </div>

      {/* Legal disclaimer - IP Compliance v2 §5 */}
      <div
        className="hidden lg:block shrink-0 py-1.5 px-3 border-t"
        style={{ borderColor: isDark ? "#1C1C1C" : "#D0D0D0" }}
      >
        <LegalDisclaimer />
      </div>

      {/* Held but unused - silences React unused-var lint in shells that
          want the prop forwarded but don't need it locally. */}
      {isMockComplete ? null : null}
    </div>
  );
}
